import React, { useState } from 'react';
import {
  TrophyIcon,
  ZapIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SkullIcon,
  CrownIcon,
  ClockIcon,
  CoinsIcon,
  CopyIcon,
  CheckIcon,
  MaximizeIcon,
  XIcon
} from 'lucide-react';
import { resolveFriendlyName } from '../shared/modelMap';
import EvolutionView from './EvolutionView';

const TournamentResults = ({ tournamentData, onClose }) => {
  const [expandedRound, setExpandedRound] = useState(null);
  const [copiedContent, setCopiedContent] = useState(null);
  const [expandedResponse, setExpandedResponse] = useState(null);

  if (!tournamentData || !tournamentData.success) {
    return null;
  }

  const {
    tournamentComplete,
    tournamentWinner,
    finalResponse,
    rounds,
    eliminationOrder,
    usage
  } = tournamentData;

  const getModelColor = (model) => {
    const key = model?.toLowerCase();
    if (key?.includes('gpt')) return 'emerald';
    if (key?.includes('claude')) return 'blue';
    if (key?.includes('gemini')) return 'purple';
    return 'slate';
  };

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-400',
      blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
      purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
      slate: 'from-slate-500/20 to-slate-600/10 border-slate-500/30 text-slate-400'
    };
    return colors[color] || colors.slate;
  };

  const handleCopy = async (content, id) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedContent(id);
      setTimeout(() => setCopiedContent(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const winnerColor = getModelColor(tournamentWinner);

  return (
    <div className="space-y-6">
      {/* Tournament Complete Banner */}
      <div className={`relative overflow-hidden glass border rounded-2xl p-6 shadow-2xl bg-gradient-to-r ${getColorClasses(winnerColor)}`}>
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/5 via-transparent to-yellow-500/5 animate-pulse"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-center gap-4 mb-4">
            <TrophyIcon className="h-10 w-10 text-yellow-400 animate-bounce" />
            <h2 className="text-3xl font-black text-white">Tournament Complete!</h2>
            <TrophyIcon className="h-10 w-10 text-yellow-400 animate-bounce" />
          </div>

          <div className="text-center">
            <p className="text-lg text-slate-300 mb-2">After 3 rounds of adversarial refinement</p>
            <div className="inline-flex items-center gap-3 px-6 py-3 glass rounded-xl border border-yellow-500/30">
              <CrownIcon className="h-8 w-8 text-yellow-400" />
              <span className="text-2xl font-black text-white">
                {resolveFriendlyName(tournamentWinner?.toLowerCase())} Wins!
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Evolution View - Interactive Score Progression */}
      {rounds && rounds.length > 0 && (
        <EvolutionView
          rounds={rounds}
          tournamentWinner={tournamentWinner}
          eliminationOrder={eliminationOrder}
          mode="tournament"
        />
      )}

      {/* Elimination Order */}
      {eliminationOrder && eliminationOrder.length > 0 && (
      <div className="glass border border-slate-700/50 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <SkullIcon className="h-6 w-6 text-red-400" />
          <h3 className="text-xl font-bold text-white">Elimination Order</h3>
        </div>

        <div className="flex items-center justify-center gap-4">
          {eliminationOrder.map((model, idx) => (
            <React.Fragment key={model}>
              <div className={`flex flex-col items-center p-4 glass rounded-xl border ${
                idx === 0 ? 'border-red-500/30' : 'border-orange-500/30'
              }`}>
                <span className="text-xs text-slate-500 mb-1">Round {idx + 1}</span>
                <SkullIcon className={`h-6 w-6 mb-1 ${idx === 0 ? 'text-red-400' : 'text-orange-400'}`} />
                <span className="font-bold text-white">{resolveFriendlyName(model)}</span>
                <span className="text-xs text-slate-400">Eliminated</span>
              </div>
              {idx < eliminationOrder.length - 1 && (
                <div className="text-slate-600 text-2xl">→</div>
              )}
            </React.Fragment>
          ))}
          <div className="text-slate-600 text-2xl">→</div>
          <div className="flex flex-col items-center p-4 glass rounded-xl border border-yellow-500/30 bg-yellow-500/10">
            <span className="text-xs text-yellow-400 mb-1">Champion</span>
            <CrownIcon className="h-6 w-6 mb-1 text-yellow-400" />
            <span className="font-bold text-yellow-300">{resolveFriendlyName(tournamentWinner?.toLowerCase())}</span>
            <span className="text-xs text-yellow-400">Winner!</span>
          </div>
        </div>
      </div>
      )}

      {/* Final Response Highlight */}
      {finalResponse && finalResponse.success && (
        <div className={`glass border rounded-2xl p-6 shadow-xl bg-gradient-to-br ${getColorClasses(winnerColor)}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <CrownIcon className="h-6 w-6 text-yellow-400" />
              <h3 className="text-xl font-bold text-white">Battle-Hardened Final Response</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleCopy(finalResponse.content, 'final')}
                className="p-2 glass rounded-lg hover:bg-white/10 transition-all"
                title="Copy response"
              >
                {copiedContent === 'final' ? (
                  <CheckIcon className="h-5 w-5 text-green-400" />
                ) : (
                  <CopyIcon className="h-5 w-5 text-slate-400" />
                )}
              </button>
              <button
                onClick={() => setExpandedResponse('final')}
                className="p-2 glass rounded-lg hover:bg-white/10 transition-all"
                title="Expand response"
              >
                <MaximizeIcon className="h-5 w-5 text-slate-400" />
              </button>
            </div>
          </div>

          <div className="bg-slate-900/50 rounded-xl p-4 max-h-64 overflow-y-auto">
            <pre className="text-slate-200 whitespace-pre-wrap font-sans text-sm leading-relaxed">
              {finalResponse.content}
            </pre>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm">
            <div className="flex items-center gap-4 text-slate-400">
              <span className="flex items-center gap-1">
                <ClockIcon className="h-4 w-4" />
                {(finalResponse.responseTime / 1000).toFixed(2)}s
              </span>
              <span className="flex items-center gap-1">
                <CoinsIcon className="h-4 w-4" />
                {finalResponse.tokens} tokens
              </span>
            </div>
            <span className="text-slate-500">
              From {resolveFriendlyName(tournamentWinner?.toLowerCase())}
            </span>
          </div>
        </div>
      )}

      {/* Round-by-Round Breakdown */}
      {rounds && rounds.length > 0 && (
      <div className="glass border border-slate-700/50 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <ZapIcon className="h-6 w-6 text-purple-400" />
          <h3 className="text-xl font-bold text-white">Round-by-Round Breakdown</h3>
        </div>

        <div className="space-y-4">
          {rounds.map((round, idx) => (
            <div
              key={idx}
              className={`glass rounded-xl border transition-all ${
                round.type === 'final' ? 'border-yellow-500/30' : 'border-slate-600/30'
              }`}
            >
              {/* Round Header */}
              <button
                onClick={() => setExpandedRound(expandedRound === idx ? null : idx)}
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-all rounded-xl"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    round.type === 'initial' ? 'bg-slate-600' :
                    round.type === 'analysis' ? 'bg-blue-600' :
                    'bg-yellow-600'
                  }`}>
                    <span className="text-white font-bold">{round.round}</span>
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-white">
                      {round.type === 'initial' ? 'Initial Battle' :
                       round.type === 'final' ? 'Championship Round' :
                       `Analysis Round`}
                    </p>
                    <p className="text-sm text-slate-400">
                      {round.type === 'initial'
                        ? 'All 3 models respond to the prompt'
                        : `Critiquing ${resolveFriendlyName(round.analyzedModel)}'s response`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {round.eliminated && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-red-500/20 rounded-full">
                      <SkullIcon className="h-4 w-4 text-red-400" />
                      <span className="text-red-300 text-sm font-medium">
                        {resolveFriendlyName(round.eliminated)} Out
                      </span>
                    </div>
                  )}
                  {round.roundWinner && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 rounded-full">
                      <TrophyIcon className="h-4 w-4 text-green-400" />
                      <span className="text-green-300 text-sm font-medium">
                        {resolveFriendlyName(round.roundWinner)}
                      </span>
                    </div>
                  )}
                  {expandedRound === idx ? (
                    <ChevronUpIcon className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-slate-400" />
                  )}
                </div>
              </button>

              {/* Expanded Content */}
              {expandedRound === idx && (
                <div className="px-4 pb-4 space-y-4 border-t border-slate-700/50 pt-4">
                  {/* Scores */}
                  <div className="grid grid-cols-3 gap-3">
                    {['gpt', 'claude', 'gemini'].map(model => {
                      const score = round.scores[model] || 0;
                      const isEliminated = round.responses?.[model]?.disqualified;
                      const isWinner = round.roundWinner?.toLowerCase() === model;
                      const modelColor = getModelColor(model);

                      return (
                        <div
                          key={model}
                          className={`p-3 rounded-xl border ${
                            isEliminated ? 'bg-slate-800/50 border-slate-700/30 opacity-50' :
                            isWinner ? `bg-gradient-to-br ${getColorClasses(modelColor)}` :
                            'bg-slate-800/50 border-slate-700/30'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-white">
                              {resolveFriendlyName(model)}
                            </span>
                            {isWinner && <CrownIcon className="h-4 w-4 text-yellow-400" />}
                            {isEliminated && <SkullIcon className="h-4 w-4 text-red-400" />}
                          </div>
                          <p className={`text-2xl font-bold ${
                            isEliminated ? 'text-slate-500' :
                            isWinner ? 'text-white' : 'text-slate-300'
                          }`}>
                            {isEliminated ? 'DQ' : Math.round(score)}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Responses Preview */}
                  {round.responses && (
                    <div className="space-y-2">
                      {['gpt', 'claude', 'gemini'].map(model => {
                        const response = round.responses[model];
                        if (!response || response.disqualified) return null;

                        return (
                          <div key={model} className="glass rounded-lg p-3 border border-slate-700/30">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-slate-300">
                                {resolveFriendlyName(model)}
                              </span>
                              <button
                                onClick={() => handleCopy(response.content, `${idx}-${model}`)}
                                className="p-1 hover:bg-white/10 rounded transition-all"
                              >
                                {copiedContent === `${idx}-${model}` ? (
                                  <CheckIcon className="h-4 w-4 text-green-400" />
                                ) : (
                                  <CopyIcon className="h-4 w-4 text-slate-500" />
                                )}
                              </button>
                            </div>
                            <p className="text-sm text-slate-400 line-clamp-3">
                              {response.content?.substring(0, 200)}...
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Usage Info */}
      {usage && (
        <div className="glass border border-slate-700/50 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-400">
            <CoinsIcon className="h-5 w-5" />
            <span>Tournament used 3 prompts</span>
          </div>
          <div className="text-slate-300">
            <span className="font-bold text-cyan-400">{usage.remaining}</span> prompts remaining
          </div>
        </div>
      )}

      {/* Expanded Response Modal */}
      {expandedResponse && finalResponse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-4xl max-h-[90vh] glass border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
              <div className="flex items-center gap-3">
                <CrownIcon className="h-6 w-6 text-yellow-400" />
                <h3 className="text-lg font-bold text-white">
                  Final Response from {resolveFriendlyName(tournamentWinner?.toLowerCase())}
                </h3>
              </div>
              <button
                onClick={() => setExpandedResponse(null)}
                className="p-2 hover:bg-white/10 rounded-lg transition-all"
              >
                <XIcon className="h-5 w-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <pre className="text-slate-200 whitespace-pre-wrap font-sans leading-relaxed">
                {finalResponse.content}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentResults;
